import { createElementFromHTML, b64toBlob } from './utils'
import { noEmptyFilter, noSameFilter } from './frameFilters'

type ImageType = 'image/png'| 'image/jpeg' | 'image/webp'

export interface IExternalTicker {
  run: (callback: () => void, fps: number) => void
  stop: () => void
}

interface IOptions {
  fps?: number
  width?: number
  height?: number
  longSideLength?: number
  type?: ImageType
  encoderOptions?: number
  canvasEl?: HTMLCanvasElement
  videoElManualPlay?: boolean
  toBlob?: boolean
  frameFilters?: Array<(ctx: CanvasRenderingContext2D) => boolean>
  errorTimeout?: number
  errorEvents?: string[]
  captureFrameMode?: 'TIME_UPDATE' | 'INTERVAL' | 'EXTERNAL_TICKER'
  externalTicker?: IExternalTicker
}

const htmlTemplate = `<canvas style="display:none;"></canvas>`

const defaultFps = 1
const defaultLongSideLength = 1280
const defaultType = 'image/jpeg'
const defaultEncoderOptions = 0.8
const defaultFrameFilters = [ noEmptyFilter, noSameFilter ]
const defaultErrorTimeoutOffset = 3000
const defaultErrorEvents = ['error']
const defaultCaptureFrameMode = 'TIME_UPDATE'

let innerCanvasEl: null | HTMLCanvasElement = null
let captureFrameTimer: any = null
let errorTimeoutTimer: any = null

export default function toImages(video: HTMLVideoElement, options: IOptions = {}): Promise<string[] | Blob[]> {
  const { fps, encoderOptions,
    videoElManualPlay, toBlob, frameFilters,
    errorTimeout, captureFrameMode,
    externalTicker } = options
  const skipVideoElPlay = videoElManualPlay
  const curCanvas = getCanvasEl(options)
  const curType = getType(options)
  const curFps = fps || defaultFps
  const curEncoderOptions =  encoderOptions || defaultEncoderOptions
  const curFrameFilters = frameFilters || defaultFrameFilters
  const curErrorTimeout = errorTimeout || video.duration * 1000 + defaultErrorTimeoutOffset
  const curCaptureFrameMode = captureFrameMode || defaultCaptureFrameMode
  const { resultWidth, resultHeight } = getResultSize(video, options)

  const ctx = curCanvas.getContext('2d') as CanvasRenderingContext2D
  const images: string[] | Blob[] = []

  curCanvas.width = resultWidth
  curCanvas.height = resultHeight

  clearAll(video, options)

  return new Promise((resolve, reject) => {
    let lastCaptureFramePromise = Promise.resolve()

    video.onplaying = async () => {
      lastCaptureFramePromise.then(() => {
        lastCaptureFramePromise = captureFrame()
      })

      if (curCaptureFrameMode === 'INTERVAL') {
        captureFrameTimer = setInterval(() => {
          lastCaptureFramePromise.then(() => {
            lastCaptureFramePromise = captureFrame()
          })
        }, 1000 / curFps)
      }

      if (curCaptureFrameMode === 'EXTERNAL_TICKER') {
        if (!externalTicker) {
          reject(new Error('Invalid ExternalTicker'))
          return
        }
        externalTicker.run(() => {
          lastCaptureFramePromise.then(() => {
            lastCaptureFramePromise = captureFrame()
          })
        }, curFps)
      }
    }

    if (curCaptureFrameMode === 'TIME_UPDATE') {
      let lastCurTimeMs = 0
      video.ontimeupdate = () => {
        const currentTimeMs = video.currentTime * 1000
        const timeMsSpace = currentTimeMs - lastCurTimeMs
        if (timeMsSpace >= 1000 / curFps) {
          lastCaptureFramePromise.then(() => {
            lastCaptureFramePromise = captureFrame()
            lastCurTimeMs = currentTimeMs
          })
        }
      }
    }

    video.onended = async () => {
      await lastCaptureFramePromise
      clearAll(video, options)
      resolve(images)
    }

    addErrorEvents(video, options, event => {
      clearAll(video, options)
      reject('To Images Error: ' + event)
    })

    errorTimeoutTimer = setTimeout(() => {
      clearAll(video, options)
      reject(new Error('To Images Timeout'))
    }, curErrorTimeout)

    if (!skipVideoElPlay) {
      video.play()
    }
  })

  async function captureFrame() {
    ctx.drawImage(video, 0, 0, resultWidth, resultHeight)

    for (const filter of curFrameFilters) {
      const result = filter(ctx)
      if (!result) {
        return
      }
    }

    if (toBlob) {
      const blob = await canvasToBlob(curCanvas, curType, curEncoderOptions)
      const blobImages = images as Blob[]
      blobImages.push(blob)
      return
    }

    const base64 = curCanvas.toDataURL(curType, curEncoderOptions)
    const base64Images = images as string[]
    base64Images.push(base64)
  }
}

export function toImagesBlob(video: HTMLVideoElement, options: IOptions = {}): Promise<Blob[]> {
  options.toBlob = true
  return toImages(video, options) as Promise<Blob[]>
}

export function toImagesBase64(video: HTMLVideoElement, options: IOptions = {}): Promise<string[]> {
  options.toBlob = false
  return toImages(video, options) as Promise<string[]>
}

export function delInnerCanvasEl() {
  if (!innerCanvasEl) {
    return
  }
  document.body.removeChild(innerCanvasEl)
  innerCanvasEl = null
}

export function getSupportImageTypes(options: {
  canvasEl?: HTMLCanvasElement,
} = {}): ImageType[] {
  const types: ImageType[] = ['image/png', 'image/jpeg']
  const canvasEl = getCanvasEl(options)

  if (canvasEl.toDataURL('image/webp').includes('data:image/webp')) {
      types.push('image/webp')
  }
  return types
}

interface IAnalyzeImagesResult {
  type: 'base64' | 'blob'
  dataType: string
  length: number
  size: number
}

export function analyzeImages(images: string[] | Blob[]): IAnalyzeImagesResult {
  const image = images[0]
  if (!image) {
    throw new Error('Invalid images')
  }

  if (typeof image === 'string') {
    const regexResult = image.match(/data:(.*);base64/)
    return {
      type: 'base64',
      dataType: regexResult && regexResult[1] ? regexResult[1] : '',
      length: images.length,
      size: (images as string[]).map(_ => _.slice(23)).join('').length / 1024,
    }
  }

  return {
    type: 'blob',
    dataType: image.type,
    length: images.length,
    size: (images as Blob[])
      .map(_ => _.size)
      .reduce((val, cur) => val + cur, 0) / 1024,
  }
}

export function base64ImagesToBlobImages(images: string[]) {
  return images.map(_ => {
    return b64toBlob(_)
  })
}

export async function blobImagesToBase64Images(images: Blob[]) {
  const resultImages: string[] = []
  for (const item of images) {
    const base64: string = await new Promise((resolve, reject) => {
      const fileReader = new FileReader()
      fileReader.onload = () => {
        resolve(fileReader.result as string)
      }
      fileReader.readAsDataURL(item)
    })
    resultImages.push(base64)
  }
  return resultImages
}

export function viewImages(images: string[] | Blob[]) {
  if (typeof images[0] === 'string') {
    return (images as string[]).map(_ => {
      const el = document.createElement('img')
      el.src = _
      return el
    })
  }
  return (images as Blob[]).map(_ => {
    const el = document.createElement('img')
    el.src = URL.createObjectURL(_)
    return el
  })
}

function getCanvasEl(options: IOptions) {
  const { canvasEl } = options
  if (canvasEl) {
    return canvasEl
  }
  if (!innerCanvasEl) {
    innerCanvasEl = createElementFromHTML<HTMLCanvasElement>(htmlTemplate)
    document.body.appendChild(innerCanvasEl)
  }
  return innerCanvasEl
}

function getType(options: IOptions) {
  const { type } = options
  const supportTypes = getSupportImageTypes()

  if (!type || !supportTypes.includes(type)) {
    return defaultType
  }
  return type
}

function getResultSize(video: HTMLVideoElement, options: IOptions) {
  const { width, height, longSideLength } = options
  const curLongSideLength = longSideLength || defaultLongSideLength
  let resultWidth = 0
  let resultWidthRatio = 0
  let resultHeightRatio = 0
  let resultHeight = 0

  // scale as width
  if (width && !height) {
    resultWidth = Math.min(width, video.videoWidth)
    resultWidthRatio = resultWidth / video.videoWidth
    resultHeight = resultWidthRatio * video.videoHeight
    return { resultWidth,  resultHeight }
  }
  // scale as height
  if (!width && height) {
    resultHeight = Math.min(height, video.videoHeight)
    resultHeightRatio = resultHeight / video.videoHeight
    resultWidth = resultHeightRatio * video.videoHeight
    return { resultWidth,  resultHeight }
  }
  // scale as longSideLength by default
  if (video.videoWidth >= video.videoHeight) {
    resultWidth = Math.min(curLongSideLength, video.videoWidth)
    resultWidthRatio = resultWidth / video.videoWidth
    resultHeight = resultWidthRatio * video.videoHeight
    return { resultWidth,  resultHeight }
  }
  resultHeight = Math.min(curLongSideLength, video.videoHeight)
  resultHeightRatio = resultHeight / video.videoHeight
  resultWidth = resultHeightRatio * video.videoHeight
  return { resultWidth,  resultHeight }
}

function clearAll(video: HTMLVideoElement, options: IOptions) {
  video.pause()
  video.currentTime = 0

  clearCaptureFrameTimer()
  clearExternalTicker(options)
  clearNormalEvent(video)
  clearErrorEvents(video, options)
  clearErrorTimeoutTimer()
}

function clearCaptureFrameTimer() {
  clearInterval(captureFrameTimer)
  captureFrameTimer = null
}

function clearExternalTicker(options: IOptions) {
  const { captureFrameMode, externalTicker } = options
  const curCaptureFrameMode = captureFrameMode || defaultCaptureFrameMode

  if (curCaptureFrameMode === 'EXTERNAL_TICKER' && externalTicker) {
    externalTicker.stop()
  }
}

function clearNormalEvent(video: HTMLVideoElement) {
  video.onplaying = null
  video.ontimeupdate = null
  video.onended = null
}

function addErrorEvents(video: HTMLVideoElement, options: IOptions, callback: (event: string) => void) {
  const { errorEvents } = options
  const events = errorEvents || defaultErrorEvents

  events.forEach(_ => {
    (video as any)['on' + _] = () => {
      callback(_)
    }
  })
}

function clearErrorEvents(video: HTMLVideoElement, options: IOptions) {
  const { errorEvents } = options
  const events = errorEvents ? [ ...defaultErrorEvents, ...errorEvents ] : [ ...defaultErrorEvents ]
  events.forEach(_ => {
    (video as any)['on' + _] = null
  })
}

function clearErrorTimeoutTimer() {
  clearTimeout(errorTimeoutTimer)
  errorTimeoutTimer = null
}

function canvasToBlob(canvas: HTMLCanvasElement, type: ImageType, encoderOptions: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      if (blob) {
        resolve(blob)
      }
    }, type, encoderOptions)
  })
}
